// IND-042: contar con tablet sobre la misma Hoja de recuento. Cada valor se
// guarda en PROA al momento y también en la tablet; si se corta la conexión, se
// reintenta solo. El estimado es una sugerencia: nunca rellena ACTUAL.
// IND-047 / IND-048: primero proveedor y material; la lista se desplaza con la
// pantalla y cada tabla del libro lleva su color.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { supabase } from '../../../config/supabaseClient';
import { getRecountEstimates, saveRecountCell } from '../services/industrialCostService';
import type {
  RecountEstimate,
  RecountRowSource,
  RecountSheetRow,
  RecountSheetView,
} from '../services/industrialCostService';
import {
  filterByTable,
  finishChecks,
  fmtQty,
  isCopied,
  isFar,
  mergeSavedRow,
  matchesSearch,
  overlayQueue,
  queueValue,
  RECOUNT_TABLES,
  tableOf,
  tableStarts,
  tabletStatus,
} from '../utils/recountSheet';
import type { QueuedValue, RecountTableKind, TabletFilter, TabletStatus } from '../utils/recountSheet';
import { useElementHeight } from '../utils/useElementHeight';
import { kgDraft, parseKg } from '../utils/wasteControl';

const MUTED = '#94a3b8';
const CYAN = '#22d3ee';
const RETRY_MS = 5_000;
const MAX_QTY = 1_000_000_000;
const ROW_COLUMNS = '16px minmax(0, 1fr) 96px 92px 104px 92px';
const KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0', '⌫'];
const STATUS: Record<TabletStatus, { text: string; color: string }> = {
  pending: { text: 'Pendiente', color: MUTED },
  counted: { text: 'Contado', color: '#86efac' },
  copied: { text: 'Copiado', color: '#67e8f9' },
  far: { text: 'Lejos de lo estimado', color: '#fbbf24' },
};

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

// IND-053: identifica a esta tablet; lo que ella misma guardó no es «otra pantalla».
function tabletDevice(): string {
  const key = 'ic-recount-device';
  const saved = readStorage<string | null>(key, null);
  if (saved) return saved;
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  writeStorage(key, id);
  return id;
}

function writeStorage(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Sin almacenamiento local, el valor sigue yendo a PROA.
  }
}

function fmtDay(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}

function estimateText(row: RecountSheetRow, estimate: RecountEstimate | undefined): string {
  if (!estimate) return '';
  if (estimate.state === 'no_code') return 'sin código';
  if (estimate.state === 'several' || estimate.state === 'none') return '≈ —';
  return `≈ ${fmtQty(estimate.estimate, row.unit === 'unidades' ? row.unit : undefined)}`;
}

export default function IndustrialCostRecountTablet({
  userId,
  year,
  month,
  view,
  onClose,
  onExport,
  exporting,
  exportFailed,
  onOpenSuppliers,
}: {
  userId: string;
  year: number;
  month: number;
  view: RecountSheetView;
  onClose: () => void;
  onExport: () => void;
  exporting: boolean;
  exportFailed: boolean;
  onOpenSuppliers: (() => void) | null;
}) {
  const sheetId = view.sheet?.id ?? '';
  // Por usuario: en una tablet compartida, lo pendiente no sale con otra sesión.
  const queueKey = `ic-recount-queue-${userId}-${sheetId}`;
  const rowKey = `ic-recount-row-${userId}-${sheetId}`;
  const queryClient = useQueryClient();
  const estimatesQuery = useQuery({
    queryKey: ['industrial-cost-recount-estimates', year, month],
    queryFn: () => getRecountEstimates(year, month),
  });
  const estimates = useMemo(() => estimatesQuery.data ?? {}, [estimatesQuery.data]);

  const [queue, setQueue] = useState<QueuedValue[]>(() => readStorage<QueuedValue[]>(queueKey, []));
  const queueRef = useRef(queue);
  const flushing = useRef(false);
  const [offline, setOffline] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const [device] = useState(tabletDevice);
  // IND-053: no enviados porque esa casilla se cambió después en otra pantalla.
  const [superseded, setSuperseded] = useState<string[]>([]);
  const [leaving, setLeaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const rows = useMemo(() => overlayQueue(view.rows, queue), [view.rows, queue]);

  const [selectedId, setSelectedId] = useState<string>(() => {
    const saved = readStorage<string | null>(rowKey, null);
    const known = saved && view.rows.some((row) => row.id === saved);
    return known ? (saved as string) : view.rows.find((row) => row.current_qty === null)?.id ?? view.rows[0]?.id ?? '';
  });
  const selected = rows.find((row) => row.id === selectedId) ?? null;
  const [draft, setDraft] = useState('');
  const [source, setSource] = useState<RecountRowSource>('tecleado');
  const [filter, setFilter] = useState<TabletFilter>('all');
  const [tableFilter, setTableFilter] = useState<RecountTableKind | 'all'>('all');
  const [toolbar, setToolbar] = useState<HTMLDivElement | null>(null);
  const toolbarHeight = useElementHeight(toolbar);
  const [search, setSearch] = useState('');
  const [finishing, setFinishing] = useState(false);

  const updateQueue = useCallback((next: QueuedValue[]) => {
    queueRef.current = next;
    setQueue(next);
    writeStorage(queueKey, next);
  }, [queueKey]);

  const flush = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      while (queueRef.current.length) {
        // IND-058: lo contado por este usuario nunca se envía con la sesión de otro
        // (p. ej. si se cambió de usuario en otra pestaña): espera en la tablet.
        const { data } = await supabase.auth.getSession();
        if (data.session?.user.id !== userId) return;
        const item = queueRef.current[0];
        try {
          const saved = await saveRecountCell(item.rowId, 'current_qty', item.value, item.source, item.writtenAt, device);
          queryClient.setQueryData<RecountSheetView>(['industrial-cost-recount-sheet', year, month], (current) =>
            current && { ...current, rows: current.rows.map((row) => (row.id === saved.id ? mergeSavedRow(row, saved) : row)) });
          setSavedAt(new Date());
        } catch (error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          // Sin conexión o fallo pasajero: se reintenta. Rechazado: no sirve
          // reintentar y no debe parar lo que viene detrás.
          if (status === 409) {
            // Hay un valor más reciente: se queda ese y se vuelve a leer la hoja.
            setSuperseded((current) => [...current, item.rowId]);
            void queryClient.invalidateQueries({ queryKey: ['industrial-cost-recount-sheet', year, month] });
          } else if (status !== 400 && status !== 404 && status !== 422) throw error;
          else setRejected((current) => [...current, item.rowId]);
        }
        // Si mientras tanto se cambió ese valor, se queda el nuevo en la cola.
        updateQueue(queueRef.current.filter((entry) => entry !== item));
        setOffline(false);
      }
    } catch {
      setOffline(true);
    } finally {
      flushing.current = false;
    }
  }, [device, month, queryClient, updateQueue, userId, year]);

  useEffect(() => {
    void flush();
    const timer = window.setInterval(() => void flush(), RETRY_MS);
    const online = () => void flush();
    window.addEventListener('online', online);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', online);
    };
  }, [flush]);

  // IND-044: al salir espera lo pendiente y cambia sola cuando termina.
  useEffect(() => {
    if (!leaving || queue.length) return;
    // Un valor rechazado se queda a la vista hasta que alguien lo lea.
    if (rejected.length || superseded.length) setLeaving(false);
    else onClose();
  }, [leaving, onClose, queue.length, rejected.length, superseded.length]);

  useEffect(() => {
    if (!selectedId) return;
    writeStorage(rowKey, selectedId);
    const row = queueRef.current.find((item) => item.rowId === selectedId);
    const stored = view.rows.find((item) => item.id === selectedId);
    setDraft(kgDraft(row ? row.value : stored?.current_qty));
    setSource('tecleado');
    document.getElementById(`recount-row-${selectedId}`)?.scrollIntoView({ block: 'nearest' });
    // Solo al cambiar de fila; lo tecleado no se pisa al llegar datos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const statusOf = (row: RecountSheetRow) => tabletStatus(row, estimates[row.id]);
  const visible = filterByTable(rows, tableFilter).filter((row) => {
    if (!matchesSearch(row, search)) return false;
    const status = statusOf(row);
    if (filter === 'pending') return status === 'pending';
    if (filter === 'copied') return isCopied(row);
    if (filter === 'far') return status === 'far';
    return true;
  });
  const count = (status: TabletStatus) => rows.filter((row) => statusOf(row) === status).length;
  const counted = rows.filter((row) => row.current_qty !== null).length;
  const parsed = parseKg(draft);
  const invalid = parsed !== null && (Number.isNaN(parsed) || parsed < 0 || parsed > MAX_QTY);
  const estimate = selected ? estimates[selected.id] : undefined;
  const index = selected ? rows.indexOf(selected) : -1;
  const starts = tableStarts(visible);

  const press = (key: string) => {
    setLeaving(false);
    setSource('tecleado');
    if (key === '⌫') setDraft((current) => current.slice(0, -1));
    else if (key === ',') setDraft((current) => (current.includes(',') ? current : `${current || '0'},`));
    else setDraft((current) => `${current}${key}`);
  };

  // Lo escrito no se pierde al cambiar de fila, terminar o salir.
  const commitDraft = () => {
    if (!selected || invalid) return;
    if (parsed !== selected.current_qty || source !== 'tecleado') {
      updateQueue(queueValue(queueRef.current, { rowId: selected.id, value: parsed, source, writtenAt: new Date().toISOString() }));
      void flush();
    }
  };

  const selectRow = (rowId: string) => {
    setLeaving(false);
    commitDraft();
    setSelectedId(rowId);
  };

  const saveAndNext = () => {
    if (!selected || invalid) return;
    commitDraft();
    const position = visible.findIndex((row) => row.id === selected.id);
    const next = visible[position + 1] ?? null;
    if (next) setSelectedId(next.id);
  };

  const checks = finishChecks(rows, estimates);
  const copied = rows.filter(isCopied).length;
  const rejectedRows = rows.filter((row) => rejected.includes(row.id)).map((row) => row.material_code);
  const supersededRows = rows.filter((row) => superseded.includes(row.id)).map((row) => row.material_code);

  return (
    <Dialog fullScreen open onClose={onClose} PaperProps={{ sx: { bgcolor: '#0b1220', backgroundImage: 'none' } }}>
      {finishing ? (
        <Stack spacing={2} sx={{ p: 3, overflow: 'auto' }}>
          <Link component="button" type="button" onClick={() => setFinishing(false)} sx={{ alignSelf: 'flex-start', fontSize: 15 }}>
            ← Volver a la hoja
          </Link>
          <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
            {`Terminar el recuento del ${fmtDay(view.sheet?.current_date_value ?? '')}`}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.5 }}>
            <Tile label="Contadas" value={`${counted} / ${rows.length}`} note={`${rows.length - counted} pendientes`} />
            <Tile label="Copiadas del mes anterior" value={copied} note="con «Copiar anterior»" color="#67e8f9" />
            <Tile label="Contadas a mano" value={counted - copied} note="con su número" color="#86efac" />
            <Tile label="Para revisar" value={count('far')} note="lejos de lo estimado" color="#fde68a" />
          </Box>
          {checks.length ? (
            <Stack spacing={1} sx={{ p: 2, borderRadius: 3, bgcolor: '#131c2e', border: '1px solid rgba(251,191,36,0.35)' }}>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#fde68a' }}>Revisa antes de terminar</Typography>
              <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
                {checks.map((check) => (
                  <Stack key={check.row.id} direction="row" spacing={1.5} justifyContent="space-between" alignItems="center" sx={{ minHeight: 52, py: 0.5 }}>
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{check.title}</Typography>
                      <Typography sx={{ fontSize: 13, color: '#cbd5e1' }}>{check.detail}</Typography>
                    </Stack>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilter('all');
                        setTableFilter('all');
                        setSearch('');
                        selectRow(check.row.id);
                        setFinishing(false);
                      }}
                      sx={{ minHeight: 44, textTransform: 'none', flexShrink: 0 }}
                    >
                      Ir a la fila
                    </Button>
                  </Stack>
                ))}
              </Box>
            </Stack>
          ) : null}
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ p: 2, borderRadius: 3, bgcolor: '#131c2e' }}>
            <Stack>
              <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Descargar para el libro</Typography>
              <Typography sx={{ fontSize: 14, color: '#cbd5e1' }}>
                {queue.length
                  ? `Quedan ${queue.length} valores por enviar a PROA: espera a que lleguen para descargar.`
                  : 'Excel con las filas en el mismo orden que la hoja. Se puede descargar con filas pendientes (salen vacías en ACTUAL).'}
              </Typography>
              {exportFailed ? <Typography sx={{ fontSize: 13, color: '#fca5a5' }}>No se pudo descargar. Inténtalo de nuevo.</Typography> : null}
            </Stack>
            <Button variant="contained" disabled={exporting || queue.length > 0} onClick={onExport} sx={{ height: 60, px: 3, textTransform: 'none', fontWeight: 800, flexShrink: 0 }}>
              Descargar Excel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack>
          {/* IND-048: sin caja con scroll interno: la lista se desplaza con la pantalla. */}
          <Box ref={setToolbar} sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: '#0b1220' }}>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25, bgcolor: '#131c2e' }}>
            <Stack>
              <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                {`Recuento del ${fmtDay(view.sheet?.current_date_value ?? '')}`}
              </Typography>
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                {`Hoja ${view.sheet?.sheet_name ?? ''} · ${
                  queue.length ? `${queue.length} sin enviar, guardados en esta tablet` : savedAt ? 'guardado · también en esta tablet' : 'también se guarda en esta tablet'
                }`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{`${counted} de ${rows.length} contadas`}</Typography>
              <Button
                disabled={leaving && !offline}
                onClick={() => {
                  if (leaving) {
                    setLeaving(false);
                    return;
                  }
                  commitDraft();
                  setLeaving(true);
                }}
                sx={{ height: 48, textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {leaving && queue.length
                  ? offline
                    ? 'Sin conexión: seguir aquí'
                    : `Enviando ${queue.length} ${queue.length === 1 ? 'valor' : 'valores'}…`
                  : 'Vista de ordenador'}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  commitDraft();
                  setFinishing(true);
                }}
                sx={{ height: 48, textTransform: 'none', fontWeight: 800 }}
              >
                Terminar
              </Button>
            </Stack>
          </Stack>
          {offline ? (
            <Alert severity="warning" sx={{ borderRadius: 0 }}>
              Sin conexión con PROA: lo contado está guardado en esta tablet y se enviará solo al volver.
            </Alert>
          ) : null}
          {rejectedRows.length ? (
            <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setRejected([])}>
              {`PROA no aceptó el valor de: ${rejectedRows.join(', ')}. Vuelve a contar esas filas.`}
            </Alert>
          ) : null}
          {supersededRows.length ? (
            <Alert severity="info" sx={{ borderRadius: 0 }} onClose={() => setSuperseded([])}>
              {`No enviado: se cambió después en otra pantalla (${supersededRows.join(', ')}). Queda el valor más reciente.`}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              placeholder="Buscar proveedor, material o código"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ width: 200 }}
            />
            {([
              ['all', `Todas · ${rows.length}`],
              ['pending', `Pendientes · ${count('pending')}`],
              ['copied', `Copiadas · ${count('copied')}`],
              ['far', `Lejos de lo estimado · ${count('far')}`],
            ] as [TabletFilter, string][]).map(([key, label]) => (
              <Button
                key={key}
                variant={filter === key ? 'contained' : 'outlined'}
                onClick={() => setFilter(key)}
                sx={{ height: 44, px: 1.25, minWidth: 0, borderRadius: 999, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {label}
              </Button>
            ))}
            {RECOUNT_TABLES.filter((table) => rows.some((row) => tableOf(row).kind === table.kind)).map((table) => (
              <Button
                key={table.kind}
                variant="outlined"
                aria-pressed={tableFilter === table.kind}
                onClick={() => setTableFilter((current) => (current === table.kind ? 'all' : table.kind))}
                sx={{
                  height: 44,
                  px: 1.25,
                  minWidth: 0,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  color: table.color,
                  borderColor: tableFilter === table.kind ? table.color : 'rgba(148,163,184,0.3)',
                  bgcolor: tableFilter === table.kind ? `${table.color}26` : 'transparent',
                }}
              >
                {`${table.label} · ${rows.filter((row) => tableOf(row).kind === table.kind).length}`}
              </Button>
            ))}
          </Stack>
          </Box>
          <Stack direction="row" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0, overflowX: 'clip' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: ROW_COLUMNS,
                  columnGap: 1,
                  pl: 2,
                  pr: 1.5,
                  py: 0.75,
                  position: 'sticky',
                  top: toolbarHeight,
                  zIndex: 2,
                  fontSize: 12,
                  fontWeight: 700,
                  color: MUTED,
                  bgcolor: '#172033',
                }}
              >
                <span />
                <span>PROVEEDOR · MATERIAL</span>
                <span style={{ textAlign: 'right' }}>ANTERIOR</span>
                <span style={{ textAlign: 'right', color: '#64748b' }}>ESTIMADO*</span>
                <span style={{ textAlign: 'right' }}>ACTUAL</span>
                <span style={{ textAlign: 'right' }}>ESTADO</span>
              </Box>
              {visible.map((row) => {
                const status = STATUS[statusOf(row)];
                const active = row.id === selectedId;
                const table = tableOf(row);
                const start = starts.get(row.id);
                return (
                  <Fragment key={row.id}>
                    {start ? (
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          fontSize: 13,
                          fontWeight: 800,
                          color: start.color,
                          bgcolor: `${start.color}14`,
                          borderLeft: `4px solid ${start.color}`,
                          borderTop: '1px solid rgba(148,163,184,0.10)',
                        }}
                      >
                        {`${start.label} · ${start.count} ${start.count === 1 ? 'fila' : 'filas'}`}
                      </Box>
                    ) : null}
                    <Box
                      id={`recount-row-${row.id}`}
                      component="button"
                      type="button"
                      onClick={() => selectRow(row.id)}
                      aria-label={`${table.label}: ${[row.supplier, row.reference ?? row.material_code].filter(Boolean).join(' · ')}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: ROW_COLUMNS,
                        columnGap: 1,
                        alignItems: 'center',
                        width: '100%',
                        minHeight: 52,
                        py: 0.5,
                        pl: 1.5,
                        pr: 1.5,
                        border: 'none',
                        borderTop: '1px solid rgba(148,163,184,0.10)',
                        borderLeft: `4px solid ${table.color}`,
                        outline: active ? `2px solid ${CYAN}` : 'none',
                        outlineOffset: -2,
                        scrollMarginTop: toolbarHeight + 40,
                        bgcolor: active ? 'rgba(34,211,238,0.10)' : 'transparent',
                        color: '#e2e8f0',
                        textAlign: 'left',
                        font: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ width: 12, height: 12, borderRadius: 999, bgcolor: status.color }} />
                      <Stack sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 15, lineHeight: 1.35 }}>
                          <strong>{row.supplier ?? 'Sin proveedor'}</strong>
                          {` · ${row.reference ?? row.material_code}`}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.3, color: MUTED }}>
                          <span style={{ color: row.supplier_code ? '#67e8f9' : '#fde68a' }}>{row.supplier_code ?? 'sin código'}</span>
                          {` · ${[row.material_code, row.destination].filter(Boolean).join(' · ')}`}
                        </Typography>
                      </Stack>
                      <Typography sx={{ textAlign: 'right', fontSize: 14, color: '#cbd5e1' }}>
                        {fmtQty(row.previous_qty, row.unit === 'unidades' ? row.unit : undefined)}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontSize: 13, fontStyle: 'italic', color: '#64748b' }}>
                        {estimateText(row, estimates[row.id])}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontSize: 17, fontWeight: 800 }}>
                        {row.current_qty === null ? '' : fmtQty(row.current_qty, row.unit === 'unidades' ? row.unit : undefined)}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: status.color }}>{status.text}</Typography>
                    </Box>
                  </Fragment>
                );
              })}
            </Box>
            {selected ? (
              <Stack
                spacing={1}
                sx={{
                  width: 380,
                  flex: '0 0 380px',
                  p: 1.5,
                  bgcolor: '#131c2e',
                  position: 'sticky',
                  top: toolbarHeight,
                  // dvh: en la tablet, sin las barras del navegador.
                  maxHeight: `calc(100dvh - ${toolbarHeight}px)`,
                  overflowY: 'auto',
                  borderLeft: `4px solid ${tableOf(selected).color}`,
                  // Con poca altura, el panel se desplaza; nada se aplasta.
                  '& > *': { flexShrink: 0 },
                }}
              >
                <Stack>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: tableOf(selected).color }}>
                    {`${tableOf(selected).label} · fila ${index + 1} de ${rows.length}`}
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3 }}>
                    {[selected.supplier ?? 'Sin proveedor', selected.reference ?? selected.material_code].join(' · ')}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    {[
                      selected.supplier_code || 'sin código',
                      selected.material_code,
                      selected.destination,
                      selected.unit === 'unidades' ? 'ud' : 'kg',
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                  <Tile label="Mes anterior" value={fmtQty(selected.previous_qty, selected.unit)} />
                  <Tile
                    label="Estimado por partes (orientativo)"
                    value={
                      estimate?.state === 'several'
                        ? '≈ —'
                        : estimate?.state === 'ok'
                          ? `≈ ${fmtQty(estimate.estimate, selected.unit)}`
                          : '—'
                    }
                  />
                </Box>
                <EstimateNote row={selected} estimate={estimate} loading={estimatesQuery.isLoading} failed={estimatesQuery.isError} onOpenSuppliers={onOpenSuppliers} />
                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ height: 64, px: 2, borderRadius: 3, border: `2px solid ${invalid ? '#f87171' : CYAN}`, bgcolor: '#0b1220' }}>
                  <Typography sx={{ fontSize: 32, fontWeight: 800 }}>{draft || ' '}</Typography>
                  <Typography sx={{ fontSize: 16, color: MUTED, ml: 1 }}>{selected.unit === 'unidades' ? 'ud' : 'kg'}</Typography>
                </Stack>
                {isFar(parsed, estimate?.estimate) ? (
                  <Typography sx={{ fontSize: 12, color: '#fbbf24' }}>Lejos de lo estimado: revísalo si quieres; se puede guardar.</Typography>
                ) : null}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                  {KEYS.map((key) => (
                    <Button key={key} variant="outlined" onClick={() => press(key)} sx={{ height: 48, fontSize: 22, fontWeight: 700, color: '#f8fafc' }}>
                      {key}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                  <Button
                    variant="outlined"
                    disabled={selected.previous_qty === null}
                    onClick={() => {
                      setDraft(kgDraft(selected.previous_qty));
                      setSource('copiado');
                    }}
                    sx={{ height: 48, textTransform: 'none', fontWeight: 700 }}
                  >
                    Copiar anterior
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setDraft('0');
                      setSource('tecleado');
                    }}
                    sx={{ height: 48, textTransform: 'none', fontWeight: 700 }}
                  >
                    0 · no hay
                  </Button>
                </Box>
                <Button variant="contained" disabled={invalid} onClick={saveAndNext} sx={{ height: 58, textTransform: 'none', fontWeight: 800, fontSize: 16 }}>
                  Guardar y siguiente →
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      )}
    </Dialog>
  );
}

function EstimateNote({
  row,
  estimate,
  loading,
  failed,
  onOpenSuppliers,
}: {
  row: RecountSheetRow;
  estimate: RecountEstimate | undefined;
  loading: boolean;
  failed: boolean;
  onOpenSuppliers: (() => void) | null;
}) {
  const sx = { fontSize: 12, color: MUTED };
  if (loading) return <Typography sx={sx}>Calculando el estimado…</Typography>;
  if (failed || !estimate || estimate.state === 'none') return <Typography sx={sx}>Sin estimado para esta fila.</Typography>;
  if (estimate.state === 'no_code') {
    return (
      <Typography sx={{ ...sx, color: '#fbbf24' }}>
        {'Proveedor sin código: '}
        {onOpenSuppliers ? (
          <Link component="button" type="button" onClick={onOpenSuppliers} sx={{ fontSize: 12 }}>poner código</Link>
        ) : 'sin sugerencia'}
      </Typography>
    );
  }
  if (estimate.state === 'several') {
    return (
      <Typography sx={sx}>
        {`Total del material ≈ ${fmtQty(estimate.material_total, row.unit)} (todas sus filas)`}
      </Typography>
    );
  }
  return (
    <Typography sx={sx}>
      {`Sugerencia, no un dato: anterior + ${fmtQty(estimate.entries, row.unit)} de albaranes de este proveedor − ${fmtQty(estimate.consumption, row.unit)} de consumo teórico de los partes`}
    </Typography>
  );
}

function Tile({ label, value, note, color }: { label: string; value: string | number; note?: string; color?: string }) {
  return (
    <Stack sx={{ p: 1.25, borderRadius: 2.5, bgcolor: 'rgba(148,163,184,0.08)' }}>
      <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: color ?? '#f8fafc' }}>{value}</Typography>
      {note ? <Typography sx={{ fontSize: 12, color: MUTED }}>{note}</Typography> : null}
    </Stack>
  );
}
